import {Fragment, memo, SyntheticEvent, useMemo} from 'react';
import {HorizontalSpacer, VerticalSpacer} from '../../atoms/spacer';
import {Avatar} from '../../atoms/avatar';
import {TextTag2} from '../../atoms/tag';
import {ProfileLink} from '../../atoms/profile-link';
import {
  Container,
  Designation,
  Footer,
  ProfileName,
  Wrapper,
} from './styles';
import {NavLinks} from '../nav-links';
import {IGroupMembers} from '../../../hooks';
import {CTA} from '../../atoms/typography/cta';
import {Groups} from '../groups';
import {FlexRowSpaceBetween} from '@ui/style/styles';
import {useNavigate} from 'react-router';
import {formatTimezoneForUser} from '@utils/timezone';

export interface UserCommon {
  name: string;
  url: string;
  avatar: string;
  id: string;
}

export interface ProfileDropdownProps {
  user: UserCommon;
  reportee: UserCommon;
  designation: string;
  groups: IGroupMembers[];
  timezone?: string;
  isAdmin: boolean;
  onClose?: () => void;
  onLogout: () => void;
  onItemClick: (
    e: SyntheticEvent<HTMLAnchorElement | HTMLButtonElement>,
    href: string,
  ) => void;
}

export const ProfileDropdown = memo(function ({
  onLogout,
  groups,
  isAdmin,
  designation,
  timezone,
  user,
  reportee,
  onClose,
}: ProfileDropdownProps) {
  const navigate = useNavigate();
  const timezoneLabel = useMemo(
    () => (timezone ? formatTimezoneForUser(timezone) : ''),
    [timezone],
  );
  const renderItem = useMemo(() => {
    return (
      <Fragment>
        <FlexRowSpaceBetween>
          <Avatar
            name={user.name}
            alt="Image"
            tooltip={false}
            src={user.avatar}
            userId={user.id}
            size="lg"
          />
        </FlexRowSpaceBetween>
        <VerticalSpacer size="12px" />
        <div>
          <ProfileName>{user.name}</ProfileName>
        </div>
        <VerticalSpacer size="10px" />
        <div>
          <Designation>
            {designation}
            <HorizontalSpacer size="8px" as="span" />
            {isAdmin && (
              <TextTag2 style={{height: '20px', padding: '1.5px 8px 0 8px'}}>
                Admin
              </TextTag2>
            )}
          </Designation>
        </div>
        <VerticalSpacer size="12px" />
        {groups.length > 0 && (
          <>
            <CTA>Group(s)</CTA>
            <VerticalSpacer size="6px" />
            <Groups items={groups} total={groups.length} />
            {/* <ProfileLink heading="Group(s)" content={department.name} /> */}
            <VerticalSpacer size="12px" />
          </>
        )}

        {timezoneLabel && (
          <>
            <ProfileLink heading="Timezone" content={timezoneLabel} notlink />
            <VerticalSpacer size="12px" />
          </>
        )}

        {reportee.id && (
          <>
            <ProfileLink
              heading="Manager"
              content={reportee.name}
              extra={
                <Avatar
                  tooltip={false}
                  alt={`${reportee.name} profile`}
                  src={reportee.avatar}
                  name={reportee.name}
                  size="sm"
                  userId={reportee.id}
                />
              }
              notlink
              href={reportee.url}
            />

            <VerticalSpacer size="8px" />
          </>
        )}
      </Fragment>
    );
  }, [
    user.name,
    user.avatar,
    user.id,
    designation,
    isAdmin,
    groups,
    timezoneLabel,
    reportee.name,
    reportee.avatar,
    reportee.id,
    reportee.url,
  ]);

  const handleHelpCenter = () => {
    window.location.assign('https://peoplebeam.myhcpage.com/en-us/');
  };

  const links = useMemo(() => {
    const workspaceNav = [
      {
        id: 'settings',
        title: 'Profile settings',
        onClick: () => {
          sessionStorage.setItem('prevurl', window.location.pathname);
          onClose?.();
          navigate('/edit');
        },
      },
      {
        id: 'help-center',
        title: 'Help Center',
        onClick: handleHelpCenter,
      },
      {id: 'log-out', title: 'Log out', onClick: onLogout},
    ];

    return workspaceNav;
  }, [onClose, onLogout, navigate]);

  return (
    <Wrapper>
      <Container>{renderItem}</Container>
      <Footer>
        <NavLinks items={links} />
      </Footer>
    </Wrapper>
  );
});
ProfileDropdown.displayName = 'ProfileDropdown';
